import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { RecruitmentCandidate } from './types';
import { recruitStatusBadge } from './recruitStatusPill';
import { RecruitmentRowActions } from './RecruitmentRowActions';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import {
  LwTable,
  LwTh,
  LwAvatar,
  LwLicenseBadges,
  LwStatusPill,
  LwButton,
  LwEmpty,
  type LwSortState
} from '@/components/lw';

interface RecruitmentTableProps {
  candidates: RecruitmentCandidate[];
  onEdit: (candidate: RecruitmentCandidate) => void;
  onTerminate?: (candidate: RecruitmentCandidate) => void;
}

type SortColumn = 'name' | 'email' | 'experience' | 'status' | 'updatedAt';

const formatDate = (dateString: string): string => {
  const date = parseAsLocalDate(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
};

export function RecruitmentTable({ candidates, onEdit, onTerminate }: RecruitmentTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<LwSortState<SortColumn>>({ col: 'updatedAt', dir: 'desc' });
  const itemsPerPage = 50;

  // Reset to the first page whenever the filtered set changes (e.g. a funnel
  // click) so we never strand the user on a now-empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [candidates]);

  const sortedCandidates = [...candidates].sort((a, b) => {
    let comparison = 0;
    switch (sort.col) {
      case 'name':
        comparison = `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        break;
      case 'email':
        comparison = a.email.localeCompare(b.email);
        break;
      case 'experience':
        comparison = (a.yearsOfExperience ?? -1) - (b.yearsOfExperience ?? -1);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'updatedAt':
        comparison = parseAsLocalDate(a.updatedAt).getTime() - parseAsLocalDate(b.updatedAt).getTime();
        break;
      default:
        comparison = 0;
    }
    return sort.dir === 'asc' ? comparison : -comparison;
  });

  const totalPages = Math.ceil(sortedCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCandidates = sortedCandidates.slice(startIndex, endIndex);

  const onSort = (col: SortColumn) => {
    setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
    setCurrentPage(1); // sorting re-orders the whole set — return to the first page
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <LwTable
        footer={
          <>
            <div className="count">
              Showing <b>{candidates.length}</b> candidate{candidates.length === 1 ? '' : 's'}
            </div>
            <div className="legend">
              <span className="legend-item">click a row to open the candidate</span>
            </div>
          </>
        }
      >
        <thead>
          <tr>
            <LwTh<SortColumn> label="Candidate" col="name" sort={sort} onSort={onSort} />
            <LwTh<SortColumn> label="Professional" col="experience" sort={sort} onSort={onSort} />
            <LwTh<SortColumn> label="Status" col="status" sort={sort} onSort={onSort} />
            <th>Recruiter</th>
            <th className="th-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentCandidates.length === 0 && (
            <tr>
              <td colSpan={5}>
                <LwEmpty title="No candidates found">Add your first candidate to get started.</LwEmpty>
              </td>
            </tr>
          )}
          {currentCandidates.map((candidate) => {
            const badge = recruitStatusBadge(candidate.status);
            return (
              <tr className="row" key={candidate.id} onClick={() => onEdit(candidate)}>
                <td>
                  <div className="pcell">
                    <LwAvatar first={candidate.firstName} last={candidate.lastName} />
                    <div>
                      <div className="pname">
                        {candidate.firstName} {candidate.lastName}
                      </div>
                      <div className="pmeta">
                        {candidate.email && <span>{candidate.email}</span>}
                        {candidate.phone && (
                          <>
                            <span className="dotsep" />
                            <span className="nmls">{candidate.phone}</span>
                          </>
                        )}
                      </div>
                      <div className="pmeta">Updated {formatDate(candidate.updatedAt)}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="pname">{candidate.position}</div>
                  <div className="pmeta">
                    <span>
                      {candidate.yearsOfExperience !== null ? `${candidate.yearsOfExperience}y exp` : 'N/A'}
                    </span>
                    <span className="dotsep" />
                    <span className="nmls">NMLS #{candidate.nmlsNumber}</span>
                  </div>
                  {candidate.stateLicenses.length > 0 && (
                    <div style={{ marginTop: 5 }}>
                      <LwLicenseBadges items={candidate.stateLicenses} />
                    </div>
                  )}
                </td>
                <td>
                  <LwStatusPill label={badge.label} badgeClass={badge.badgeClass} />
                </td>
                <td>
                  {candidate.recruiterName ? (
                    <div>
                      <div className="rec-name">{candidate.recruiterName}</div>
                      {candidate.recruiterEmail && <div className="pmeta">{candidate.recruiterEmail}</div>}
                    </div>
                  ) : (
                    <span className="rec-unassigned">Unassigned</span>
                  )}
                </td>
                <td className="actcell" onClick={(e) => e.stopPropagation()}>
                  <RecruitmentRowActions candidate={candidate} onEdit={onEdit} onTerminate={onTerminate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </LwTable>

      {totalPages > 1 && (
        <div className="toolbar" style={{ justifyContent: 'space-between', marginTop: 14 }}>
          <div className="result-count">
            Showing {startIndex + 1}-{Math.min(endIndex, candidates.length)} of {candidates.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <LwButton variant="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <ChevronLeft size={15} /> Previous
            </LwButton>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <LwButton
                  key={pageNum}
                  variant={currentPage === pageNum ? 'sm-solid' : 'sm'}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </LwButton>
              );
            })}
            <LwButton
              variant="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={15} />
            </LwButton>
          </div>
        </div>
      )}
    </>
  );
}
